/**
 * Typed API client for /api/v1/integrations
 * Matches the ARCHITECTURE.md contract exactly.
 */
import { apiClient } from './client.js';

export type IntegrationKind =
  | 'connectwise'
  | 'microsoft365'
  | 'teams'
  | 'threecx'
  | 'ninjarmm'
  | 'sophos'
  | 'claude'
  | 'azure_maps'
  | 'speech'
  | 'telnyx';

export type IntegrationStatus = 'connected' | 'error' | 'pending' | 'disconnected';
export type HealthStatus = 'healthy' | 'degraded' | 'error' | 'unknown' | 'connecting';

export interface IntegrationConnection {
  id: string;
  tenantId: string;
  kind: IntegrationKind;
  status: IntegrationStatus;
  health: HealthStatus;
  displayName: string;
  /** Non-secret config (URLs, tenant IDs, etc.) */
  config: Record<string, string>;
  /** Masked hint — last 4 chars of the secret; never the full secret */
  secretHint?: string;
  lastTestedAt?: string;
  lastSyncAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationListResponse {
  items: IntegrationConnection[];
  nextCursor?: string;
}

export interface ConnectInput {
  kind: IntegrationKind;
  displayName: string;
  config: Record<string, string>;
  secret: string;
}

export interface UpdateInput {
  displayName?: string;
  config?: Record<string, string>;
  secret?: string;
}

export interface TestResult {
  ok: boolean;
  message: string;
  detail?: string;
  latencyMs?: number;
  /** True when this provider's live test is not implemented yet (creds were saved fine). */
  notImplemented?: boolean;
}

export interface RotateInput {
  secret: string;
}

export const integrationsApi = {
  /** List all integrations for the current tenant */
  list: (cursor?: string) =>
    apiClient.get<IntegrationListResponse>('/integrations', {
      params: { cursor },
    }),

  /** Get a single integration */
  get: (id: string) =>
    apiClient.get<IntegrationConnection>(`/integrations/${id}`),

  /** Connect / create a new integration */
  connect: (input: ConnectInput) =>
    apiClient.post<IntegrationConnection>('/integrations', input),

  /** Update config or secret */
  update: (id: string, input: UpdateInput) =>
    apiClient.patch<IntegrationConnection>(`/integrations/${id}`, input),

  /** Test connection (re-runs health check) */
  test: (id: string) =>
    apiClient.post<TestResult>(`/integrations/${id}/test`),

  /** Rotate the stored secret */
  rotate: (id: string, input: RotateInput) =>
    apiClient.post<IntegrationConnection>(`/integrations/${id}/rotate`, input),

  /** Disconnect (soft-delete) */
  disconnect: (id: string) =>
    apiClient.delete<void>(`/integrations/${id}`),
};
